import React from 'react';
import cx from 'classnames';
import ReactECharts from 'echarts-for-react';
import { Flex, Pagination, Progress, Typography } from 'antd';

import { IReportPageData } from '@/constants/geo';
import SVGLeftArrow from '@/assets/geo/pc/leftArrow.svg?react';
import SVGRightArrow from '@/assets/geo/pc/rightArrow.svg?react';
import LongText from '@/components/LongText';

import { uiPxToRem, uiPxToPx } from '@/utils/mobile';

import {
  getVisibilityChartOption,
  getDonutOption,
  getStackedBarOption,
  getCombinedDonutStackedBarOption,
} from '../../chartConfig/mobileChartOptions';
import styles from './index.mobile.module.less';
import classNames from 'classnames';
import ReportLayout from '@/components/ReportLayout/ReportLayout';

const { Text } = Typography;
/** 倾向性标签 */
const INCLINATION_LABELS = ['积极', '中立', '消极'];
/** 饼图样式 */
const DONUT_STYLES: React.CSSProperties = {
  // height: uiPxToRem(168),
  // width: uiPxToRem(168),
  width: '100%',
  height: '100%',
};

/** 柱状图样式 */
const BAR_STYLES: React.CSSProperties = {
  // height: uiPxToRem(224),
  height: '100%',
  width: '100%',
};

interface IProps {
  pageData: IReportPageData;
  setPageData?: (data: Partial<IReportPageData>) => void;
}

const MobileReport: React.FC<IProps> = (props) => {
  const { pageData, setPageData } = props;
  const {
    aiPlatformNameList,
    aiInclinationNumber,
    brandCompetitivenessData,
    brandCompetitivenessPlatformChartData,
    aiVisibilityPlatformNumberData,
    aiInclinationPlatformChartData,
  } = pageData;

  /** 渲染左侧圆形图 */
  const renderRound = (config: {
    title: string;
    desc: string;
    roundNode: React.ReactNode;
    className?: string;
    barNode: React.ReactNode;
  }) => {
    const { title, desc, roundNode, className, barNode } = config;
    return (
      <div className={cx(styles['mr_part'], leftRoundClass, className)}>
        <div className={styles['mr_common_title']}>{title}</div>
        <div className={styles['mr_common_desc']}>{desc}</div>
        <Flex style={{ height: uiPxToRem(200) }} align="center" justify="space-between">
          <div style={{ width: '40%', height: '100%' }}>{roundNode}</div>
          <div style={{ width: '60%', height: '100%' }}>{barNode}</div>
        </Flex>
      </div>
    );
  };

  /** 渲染AI可见性 */
  const renderRoundContent = () => {
    return (
      <div className={styles['mr_visibility_wrap']} style={DONUT_STYLES}>
        <Progress
          type="circle"
          percent={100}
          // percent={pageData.aiVisibilityNumber}
          // width={uiPxToPx(120)}
          strokeWidth={uiPxToPx(8)}
          format={() => (
            <div className={styles['mr_visibility_inner']}>
              <div className={styles['mr_visibility']}>
                <div className={styles['mr_visibility_value']}>{pageData.aiVisibilityNumber}</div>
                {/* <div className={styles['mr_visibility_unit']}>%</div> */}
              </div>
            </div>
          )}
          strokeColor="#FA8846"
          railColor="#DFE0E5"
        />
      </div>
    );
  };

  /** 渲染左侧饼图 */
  const renderPieChart = (config: { data: number[]; legend: string[]; colors?: string[] }) => {
    const { data, legend, colors } = config;
    return (
      <div className={styles['mr_pie_chart_wrap']} style={DONUT_STYLES}>
        <ReactECharts
          option={getDonutOption({ data, legend, colors })}
          className={styles['mr_pie_chart']}
          style={DONUT_STYLES}
        />
        <div className={styles['mr_pie_center']}>
          <div className={styles['mr_pie_center_value_wrap']}>
            <div className={styles['mr_pie_center_value']}>{data[0]}</div>
            <div className={styles['mr_pie_center_unit']}>%</div>
          </div>
          <div className={styles['mr_pie_center_legend']}>{legend[0]}占比</div>
        </div>
      </div>
    );
  };

  /** 左侧饼图 class */
  const leftRoundClass = styles['mr_left_round'];
  /** 右侧图表 class */
  const rightChartClass = styles['mr_right_chart'];
  return (
    <>
      <div className={styles['mr_part_wrap']}>
        {renderRound({
          title: '品牌搜索指数',
          desc: 'AI平台搜索时的品牌热度、信息权重和可见度综合计算结果',
          roundNode: renderRoundContent(),
          className: styles['mr_left_round_visibility'],
          barNode: (
            <ReactECharts
              option={getVisibilityChartOption({
                legend: aiPlatformNameList,
                data: aiVisibilityPlatformNumberData,
                isMobile: true,
              })}
              style={BAR_STYLES}
            />
          ),
        })}
        {/* <div className={cx(styles['mr_part'], rightChartClass)}>
            <div className={styles['mr_common_title']}>各平台曝光情况</div>
            <ReactECharts
              option={getVisibilityChartOption({
                legend: aiPlatformNameList,
                data: aiVisibilityPlatformNumberData,
                isMobile: true,
              })}
              style={BAR_STYLES}
            />
          </div> */}
      </div>
      <div className={styles['mr_part_wrap']}>
        {renderRound({
          title: '品牌舆情度',
          desc: 'AI推荐时的正面、负面、中立评价信息占比',
          roundNode: renderPieChart({
            data: [
              aiInclinationNumber.positivePercent,
              aiInclinationNumber.neutralPercent,
              aiInclinationNumber.negativePercent,
            ],
            legend: INCLINATION_LABELS,
            colors: ['#FA8846', '#5D8AF2', '#666A80'],
          }),
          barNode: (
            <ReactECharts
              option={getStackedBarOption({
                legend: aiPlatformNameList,
                data: aiInclinationPlatformChartData,
                isMobile: true,
              })}
              style={BAR_STYLES}
            />
          ),
        })}
      </div>
      <div className={styles['mr_part_wrap']}>
        <div className={cx(styles['mr_part'], leftRoundClass)}>
          <div className={styles['mr_common_title']}>品牌竞争度</div>
          <div className={styles['mr_common_desc']}>AI推荐时品牌在TOP品牌中的信息占比</div>
          <Flex style={{ height: uiPxToRem(200), position: 'relative' }} align="center" justify="space-between">
            <ReactECharts
              option={getCombinedDonutStackedBarOption({
                donutConfig: {
                  data: brandCompetitivenessData.map((item) => item.percentage),
                  legend: brandCompetitivenessData.map((item) => item.name),
                },
                stackedBarConfig: {
                  legend: aiPlatformNameList,
                  data: brandCompetitivenessPlatformChartData,
                },
              })}
              style={BAR_STYLES}
            />
            <div
              className={styles['mr_pie_center']}
              style={{
                left: '20%',
                top: '45%',
              }}
            >
              <div className={styles['mr_pie_center_value_wrap']}>
                <div className={styles['mr_pie_center_value']}>
                  {brandCompetitivenessData.find((item) => item.name === '本品牌')?.percentage}
                </div>
                <div className={styles['mr_pie_center_unit']}>%</div>
              </div>
              <div className={styles['mr_pie_center_legend']}>本品牌占比</div>
            </div>
          </Flex>
        </div>
      </div>
    </>
  );
};

export default MobileReport;
